import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { fmtDate } from "@/lib/format";
import { UserPlus, ShieldCheck, Key, Trash, PencilSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "staff", password: "" });
  const [resetOpen, setResetOpen] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [editOpen, setEditOpen] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "staff" });

  const load = useCallback(async () => { const { data } = await api.get("/users"); setUsers(data); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { await api.post("/users", form); toast.success("User created — they must change password on first login"); setOpen(false); setForm({ email: "", name: "", role: "staff", password: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleActive = async (u) => { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); };
  const changeRole = async (u, role) => { await api.patch(`/users/${u.id}`, { role }); toast.success("Role updated"); load(); };
  const resetPassword = async () => { await api.patch(`/users/${resetOpen}`, { reset_password: resetPw }); toast.success("Password reset — user must change on next login"); setResetOpen(null); setResetPw(""); };
  const openEdit = (u) => { setEditForm({ name: u.name, email: u.email, role: u.role }); setEditOpen(u.id); };
  const saveEdit = async () => {
    try { await api.patch(`/users/${editOpen}`, editForm); toast.success("User updated"); setEditOpen(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Users" subtitle="Manual account creation. No public signup."
        actions={<Button data-testid="new-user-btn" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white"><UserPlus size={18} weight="bold" className="mr-2" />New User</Button>} />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <tr><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Role</th><th className="px-4 py-3 text-left hidden sm:table-cell">Created</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                <td className="px-4 py-3 text-slate-600 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="text-sm border border-slate-200 rounded px-2 py-1 bg-white capitalize" data-testid={`role-${u.id}`}>
                    <option value="admin">admin</option><option value="staff">staff</option>
                  </select>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell text-slate-500">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(u)} className={`text-xs px-2 py-0.5 rounded-full border ${u.active ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                    {u.active ? "active" : "disabled"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(u)} className="p-1.5 text-slate-400 hover:text-slate-900" title="Edit user" data-testid={`edit-user-${u.id}`}><PencilSimple size={16} /></button>
                  <button onClick={() => setResetOpen(u.id)} className="p-1.5 text-slate-400 hover:text-slate-900 ml-1" title="Reset password" data-testid={`reset-${u.id}`}><Key size={16} /></button>
                  {u.email !== user?.email && (
                    <button onClick={() => del(u)} className="p-1.5 text-slate-400 hover:text-red-600 ml-1" title="Delete user" data-testid={`delete-user-${u.id}`}><Trash size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">New user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Fld label="Full name"><input data-testid="user-name" className="cd-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Fld>
            <Fld label="Email"><input data-testid="user-email" className="cd-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Fld>
            <Fld label="Role"><select data-testid="user-role" className="cd-input capitalize" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="staff">staff</option><option value="admin">admin</option></select></Fld>
            <Fld label="Temporary password"><input data-testid="user-password" className="cd-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Fld>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="user-save" onClick={create} className="bg-slate-900 text-white hover:bg-slate-800">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editOpen} onOpenChange={(o) => !o && setEditOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Edit user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Fld label="Full name"><input data-testid="edit-user-name" className="cd-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></Fld>
            <Fld label="Email"><input data-testid="edit-user-email" className="cd-input" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></Fld>
            <Fld label="Role"><select data-testid="edit-user-role" className="cd-input capitalize" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}><option value="staff">staff</option><option value="admin">admin</option></select></Fld>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(null)}>Cancel</Button><Button data-testid="edit-user-save" onClick={saveEdit} className="bg-slate-900 text-white hover:bg-slate-800">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetOpen} onOpenChange={(o) => !o && setResetOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Reset password</DialogTitle></DialogHeader>
          <Fld label="New temporary password"><input data-testid="reset-pw" className="cd-input" value={resetPw} onChange={(e) => setResetPw(e.target.value)} /></Fld>
          <DialogFooter><Button variant="outline" onClick={() => setResetOpen(null)}>Cancel</Button><Button onClick={resetPassword} className="bg-slate-900 text-white hover:bg-slate-800">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Fld({ label, children }) { return <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label><div className="mt-1">{children}</div></div>; }
